import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FinancialYearProvider } from './contexts/FinancialYearContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OfflineSyncProvider } from './contexts/OfflineSyncContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';
import ModuleAccessRoute from './components/auth/ModuleAccessRoute';
import SuperAdminPermissionRoute from './components/auth/SuperAdminPermissionRoute';
import { SUPER_ADMIN_PERMISSIONS as SAP } from './constants/superAdminPermissions';
import { resolveTenantId } from './utils/tenantIdentity';
import BusinessSuiteAccessRoute from './hdOrchids/components/BusinessSuiteAccessRoute';

// ============================================
// SUPER ADMIN PAGES
// ============================================

// ============================================
// COMPANY ADMIN PAGES
// ============================================

// ============================================
// SETTINGS MODULE
// ============================================

// ============================================
// GROWER MODULE (Replaces Customer)
// ============================================

// ============================================
// CATEGORIES MODULE
// ============================================

// ============================================
// PRODUCT MODULE
// ============================================

// ============================================
// DISPATCH MODULE
// ============================================

// ============================================
// WAREHOUSE MODULE
// ============================================

// ============================================
// TRADING MODULE
// ============================================

// ============================================
// INVENTORY MODULE
// ============================================

// ============================================
// OUTWARD MANAGEMENT - ROLE BASED FLOW
// ============================================
// Canonical path: make sure the file itself is named OutwardDetail.jsx.

// ============================================
// INWARD MANAGEMENT - ROLE BASED FLOW
// ============================================

// Step 1: Gate Entry

// Step 2: Weighment

// Step 3: Combined Unloading & Inspection


// Step 4: Lot Creation

// Step 5: Storage Allocation


// ============================================
// STORAGE MANAGEMENT MODULE
// ============================================

// ============================================
// AGREEMENT MODULE
// ============================================

// ============================================
// INVOICE MODULE
// ============================================

// ============================================
// FINANCE MODULE - NEW CONSOLIDATED MODULE
// ============================================

// ============================================
// EMPLOYEE MODULE
// ============================================

// ============================================
// TEMPERATURE / IOT MONITORING MODULE
// ============================================

// ============================================
// COMPANY REPORTING MODULE - ROLE BASED
// ============================================
import { COMPANY_REPORT_ROUTES } from './pages/reports/reportRoutes';

// COMPANY NOTIFICATIONS


// Lazy-loaded route pages: keeps the initial bundle and route switches fast.
const SuperAdminLogin = lazy(() => import('./pages/SuperAdminLogin'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Companies = lazy(() => import('./pages/superadmin/Companies'));
const Subscriptions = lazy(() => import('./pages/superadmin/Subscriptions'));
const Addons = lazy(() => import('./pages/superadmin/Addons'));
const Plans = lazy(() => import('./pages/superadmin/Plans'));
const Settings = lazy(() => import('./pages/superadmin/Settings'));
const Reports = lazy(() => import('./pages/superadmin/Reports'));
const SuperAdminEmployeeManagement = lazy(() => import('./pages/superadmin/EmployeeManagement'));
const PaymentsReceipts = lazy(() => import('./pages/superadmin/PaymentsReceipts'));
const PlatformCRM = lazy(() => import('./pages/superadmin/PlatformCRM'));
const SuperAdminSupport = lazy(() => import('./pages/superadmin/SuperAdminSupport'));
const CompanyLogin = lazy(() => import('./pages/company/CompanyLogin'));
const CompanyDashboard = lazy(() => import('./pages/company/CompanyDashboard'));
const BusinessSuiteLanding = lazy(() => import('./hdOrchids/pages/BusinessSuiteLanding'));
const MixedSuiteLanding = lazy(() => import('./hdOrchids/pages/MixedSuiteLanding'));
const HdOrchidsWorkspace = lazy(() => import('./hdOrchids/pages/HdOrchidsWorkspace'));
const CompanySettings = lazy(() => import('./pages/company/Settings'));
const BranchManagement = lazy(() => import('./pages/company/BranchManagement'));
const AuditTrail = lazy(() => import('./pages/company/AuditTrail'));
const PremiumAddons = lazy(() => import('./pages/company/PremiumAddons'));
const CompanySupport = lazy(() => import('./pages/company/Support'));
const SubscriptionReceipts = lazy(() => import('./pages/company/SubscriptionReceipts'));
const CustomizationCenter = lazy(() => import('./pages/company/CustomizationCenter'));
const FinancialYearManagement = lazy(() => import('./pages/settings/FinancialYearManagement'));
const FormFieldSettings = lazy(() => import('./pages/settings/FormFieldSettings'));
const LabelDesigner = lazy(() => import('./pages/settings/LabelDesigner'));
const GrowerList = lazy(() => import('./pages/growers/Growers'));
const AddGrower = lazy(() => import('./pages/growers/AddGrower'));
const GrowerOperationsLedger = lazy(() => import('./pages/growers/GrowerLedger'));
const GrowerDashboard = lazy(() => import('./pages/growers/GrowerDashboard'));
const GrowerPortalDashboard = lazy(() => import('./pages/growers/GrowerPortalDashboard'));
const GrowerPortalLogin = lazy(() => import('./pages/growers/GrowerPortalLogin'));
const CategoryList = lazy(() => import('./pages/categories/CategoryList'));
const ProductList = lazy(() => import('./pages/products/ProductList'));
const AddProduct = lazy(() => import('./pages/products/AddProduct'));
const WarehouseList = lazy(() => import('./pages/warehouse/WarehouseList'));
const ChamberLocationManager = lazy(() => import('./pages/warehouse/ChamberLocationManager'));
const TradingDashboard = lazy(() => import('./pages/trading/TradingDashboard.jsx'));
const TradeForm = lazy(() => import('./pages/trading/TradeForm.jsx'));
const TradingReports = lazy(() => import('./pages/trading/TradingReports.jsx'));
const InventoryList = lazy(() => import('./pages/inventory/InventoryList'));
const Transfer = lazy(() => import('./pages/inventory/Transfer'));
const Adjustment = lazy(() => import('./pages/inventory/Adjustment'));
const StockManagement = lazy(() => import('./pages/inventory/stock/StockManagement'));
const StockOwnershipTransfer = lazy(() => import('./pages/inventory/StockOwnershipTransfer'));
const OutwardDashboard = lazy(() => import('./pages/inventory/outward/OutwardDashboard'));
const OutwardProcess = lazy(() => import('./pages/inventory/outward/OutwardProcess'));
const OutwardRequest = lazy(() => import('./pages/inventory/outward/OutwardRequest'));
const GradingReceipt = lazy(() => import('./pages/inventory/outward/GradingReceipt'));
const GradingLineRuns = lazy(() => import('./pages/inventory/outward/GradingLineRuns'));
const OutwardReports = lazy(() => import('./pages/inventory/outward/OutwardReports'));
const OutwardDetail = lazy(() => import('./pages/inventory/outward/OutwardDetail'));
const Inward = lazy(() => import('./pages/inventory/Inward'));
const InwardDashboard = lazy(() => import('./pages/inventory/inward/InwardDashboard'));
const InwardWorkflowWorkbench = lazy(() => import('./pages/inventory/inward/InwardWorkflowWorkbench'));
const PublicInwardRequest = lazy(() => import('./pages/public/PublicInwardRequest'));
const PublicInwardRequests = lazy(() => import('./pages/inventory/inward/PublicInwardRequests'));
const InwardDetail = lazy(() => import('./pages/inventory/inward/InwardDetail'));
const InwardProcess = lazy(() => import('./pages/inventory/inward/InwardProcess'));
const GateEntry = lazy(() => import('./pages/inventory/inward/GateEntry'));
const WeighbridgeEntry = lazy(() => import('./pages/inventory/inward/WeighbridgeEntry'));
const CombinedWeighmentInspection = lazy(() => import('./pages/inventory/inward/CombinedWeighmentInspection'));
const Unloading = lazy(() => import('./pages/inventory/inward/Unloading'));
const InwardGrading = lazy(() => import('./pages/inventory/inward/InwardGrading'));
const InwardFinance = lazy(() => import('./pages/inventory/inward/InwardFinance'));
const InwardPacking = lazy(() => import('./pages/inventory/inward/InwardPacking'));
const LotCreation = lazy(() => import('./pages/inventory/inward/LotCreation'));
const StorageAllocation = lazy(() => import('./pages/inventory/inward/StorageAllocation'));
const StorageDashboard = lazy(() => import('./pages/inventory/storage/StorageDashboard'));
const BinManagement = lazy(() => import('./pages/inventory/bins/BinManagement'));
const BinDashboard = lazy(() => import('./pages/inventory/bins/BinDashboard'));
const BinDetail = lazy(() => import('./pages/inventory/bins/BinDetail'));
const BulkCreateBinsPage = lazy(() => import('./pages/inventory/bins/BulkCreateBinsPage'));
const CrateManagement = lazy(() => import('./pages/inventory/crates/CrateManagement'));
const CrateLedger = lazy(() => import('./pages/inventory/crates/CrateLedger'));
const CrateReconciliation = lazy(() => import('./pages/inventory/crates/CrateReconciliation'));
const AgreementList = lazy(() => import('./pages/agreements/AgreementList'));
const CreateAgreement = lazy(() => import('./pages/agreements/CreateAgreement'));
const AgreementView = lazy(() => import('./pages/agreements/AgreementView'));
const InvoiceList = lazy(() => import('./pages/billing/InvoiceList'));
const CreateInvoice = lazy(() => import('./pages/billing/CreateInvoice'));
const InvoiceDetail = lazy(() => import('./pages/billing/InvoiceDetail'));
const FinanceDashboard = lazy(() => import('./pages/finance/FinanceDashboard'));
const FinanceInvoices = lazy(() => import('./pages/finance/Invoices'));
const FinanceReceipts = lazy(() => import('./pages/finance/Receipts'));
const FinancePayments = lazy(() => import('./pages/finance/Payments'));
const FinanceGrowerLedger = lazy(() => import('./pages/finance/GrowerLedger'));
const FinanceExpenses = lazy(() => import('./pages/finance/Expenses'));
const FinanceAccounts = lazy(() => import('./pages/finance/Accounts'));
const BankReconciliation = lazy(() => import('./pages/finance/BankReconciliation'));
const EmployeeManagement = lazy(() => import('./pages/employees/EmployeeManagement'));
const EmployeeFormWrapper = lazy(() => import('./pages/employees/EmployeeFormWrapper'));
const EmployeeViewPage = lazy(() => import('./pages/employees/EmployeeViewPage'));
const AttendanceDashboard = lazy(() => import('./pages/employees/AttendanceDashboard'));
const SelfieAttendance = lazy(() => import('./pages/employees/SelfieAttendance'));
const SelfieAttendanceReport = lazy(() => import('./pages/employees/SelfieAttendanceReport'));
const PayrollManagement = lazy(() => import('./pages/employees/PayrollManagement'));
const EmployeeStats = lazy(() => import('./pages/employees/EmployeeStats'));
const UnloadingWorkDashboard = lazy(() => import('./pages/employees/UnloadingWorkDashboard'));
const EmployeeAdvancesLoans = lazy(() => import('./pages/employees/EmployeeAdvancesLoans'));
const TemperatureDashboard = lazy(() => import('./pages/temperature/TemperatureDashboard'));
const SensorConfiguration = lazy(() => import('./pages/temperature/SensorConfiguration'));
const TemperatureAlerts = lazy(() => import('./pages/temperature/TemperatureAlerts'));
const ReportsDashboard = lazy(() => import('./pages/reports/ReportsDashboard'));
const FinanceReportsDashboard = lazy(() => import('./pages/reports/FinanceReportsDashboard'));
const GrowerReportCenter = lazy(() => import('./pages/reports/GrowerReportCenter'));
const NotificationCenter = lazy(() => import('./pages/notifications/NotificationCenter.jsx'));
const NotificationSettings = lazy(() => import('./pages/notifications/NotificationSettings.jsx'));
const TenantIntegrations = lazy(() => import('./pages/integrations/TenantIntegrations'));
const ColdStorageCRM = lazy(() => import('./pages/crm/ColdStorageCRM'));
const ServiceProviders = lazy(() => import('./pages/serviceproviders/ServiceProviders'));
const InternalDocuments = lazy(() => import('./pages/documents/InternalDocuments'));
const MainLayout = lazy(() => import('./components/layout/MainLayout'));

// ============================================
// STYLES
// ============================================
import './styles/globals.css';
import './styles/glassmorphism.css';

/* --------------------------------------------------------------------------
   Route helpers
-------------------------------------------------------------------------- */

/**
 * <Navigate> does NOT interpolate route params. `<Navigate to="/growers/:id" />`
 * sends the user to the literal path "/growers/:id", which then falls through
 * to the catch-all. These wrappers read the param and build a real URL.
 */
const RedirectGrower = ({ suffix = '' }) => {
  const { id } = useParams();
  return <Navigate to={`/growers/${id}${suffix}`} replace />;
};

/** Preserve query parameters on compatibility redirects. */
const RedirectWithSearch = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search || ''}`} replace />;
};

/** Preserve the dispatch/outward id instead of dropping it on legacy URLs. */
const RedirectOutwardById = ({ process = false }) => {
  const { id } = useParams();
  const { search } = useLocation();
  return (
    <Navigate
      to={`/outward/${id}${process ? '/process' : ''}${search || ''}`}
      replace
    />
  );
};

/** Deep-link compatibility for old inward step URLs. */
const RedirectInwardById = () => {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/inward/${id}/process${search || ''}`} replace />;
};

/** Preserve legacy /inventory/bins/:binId deep links. */
const RedirectStorageBin = ({ edit = false }) => {
  const { binId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/storage/bins/${binId}${edit ? '/edit' : ''}${search || ''}`} replace />;
};

/**
 * Catch-all destination.
 *
 * /dashboard is a super-admin route. Sending a company user there means
 * ProtectedRoute bounces them again, which reads to the user as
 * "the page sent me back to the dashboard". Route by account type instead.
 */
const NotFoundRedirect = () => {
  const { userData } = useAuth();

  const isCompanyUser = Boolean(resolveTenantId(userData));

  return (
    <Navigate
      to={isCompanyUser ? '/company-dashboard' : '/dashboard'}
      replace
    />
  );
};

const SuperAdminAccessRoute = ({ children, permissions }) => (
  <ProtectedRoute requireSuperAdmin={true}>
    <SuperAdminPermissionRoute permissions={permissions}>
      {children}
    </SuperAdminPermissionRoute>
  </ProtectedRoute>
);

const CompanyAccessRoute = ({
  children,
  permissions,
  allOf,
  anyOf,
}) => (
  <ProtectedRoute requireSuperAdmin={false}>
    <BusinessSuiteAccessRoute>
      <ModuleAccessRoute>
        <RoleProtectedRoute
          permissions={permissions}
          allOf={allOf}
          anyOf={anyOf}
        >
          {children}
        </RoleProtectedRoute>
      </ModuleAccessRoute>
    </BusinessSuiteAccessRoute>
  </ProtectedRoute>
);

// Grower self-service uses the same tenant/subscription and module checks but
// intentionally does not inherit Store employee permissions. The authenticated
// user must still be linked to a grower record inside GrowerPortalDashboard.
const GrowerPortalAccessRoute = ({ children }) => (
  <ProtectedRoute requireSuperAdmin={false}>
    <ModuleAccessRoute>{children}</ModuleAccessRoute>
  </ProtectedRoute>
);

// Keep the current layout visible while lazy route chunks load; avoid a
// second full-page loading animation for each route transition.
const RouteLoadingFallback = () => null;

const AppRoutes = () => (
  <Routes>
    {/* ============================================
        PUBLIC ROUTES
    ============================================ */}
    {/* ============================================
        AUTHENTICATION PORTALS
        ============================================

        Canonical Super Admin login:
          /admin/superadmin/login

        Canonical Company / Store login:
          /login/storelogin

        Legacy login URLs are preserved below as redirects so existing
        bookmarks, old buttons and shared links keep working.
    */}

    {/* Super Admin Login - PUBLIC */}
    <Route
      path="/admin/superadmin/login"
      element={<SuperAdminLogin />}
    />

    {/* Company / Store Login - PUBLIC */}
    <Route
      path="/login/storelogin"
      element={<CompanyLogin />}
    />


    <Route path="/grower-login" element={<GrowerPortalLogin />} />

    {/* Legacy authentication URL compatibility */}
    <Route
      path="/login"
      element={
        <Navigate
          to="/admin/superadmin/login"
          replace
        />
      }
    />

    <Route
      path="/company-login"
      element={
        <Navigate
          to="/login/storelogin"
          replace
        />
      }
    />

    {/* Extra compatibility aliases */}
    <Route
      path="/superadmin/login"
      element={
        <Navigate
          to="/admin/superadmin/login"
          replace
        />
      }
    />

    <Route
      path="/admin/login"
      element={
        <Navigate
          to="/admin/superadmin/login"
          replace
        />
      }
    />

    <Route
      path="/store-login"
      element={
        <Navigate
          to="/login/storelogin"
          replace
        />
      }
    />

    {/* PUBLIC GROWER / CUSTOMER INWARD FORM
        IMPORTANT: no ProtectedRoute, no CompanyAccessRoute, no MainLayout. */}
    <Route
      path="/public/inward-request"
      element={<PublicInwardRequest />}
    />

    <Route path="/" element={<Navigate to="/login/storelogin" replace />} />

    {/* ============================================
        SUPER ADMIN ROUTES
    ============================================ */}
    <Route path="/dashboard" element={<SuperAdminAccessRoute permissions={[SAP.DASHBOARD_VIEW]}><Dashboard /></SuperAdminAccessRoute>} />

    <Route path="/companies" element={<SuperAdminAccessRoute permissions={[SAP.COMPANIES_VIEW, SAP.COMPANIES_MANAGE]}><Companies /></SuperAdminAccessRoute>} />


    <Route path="/addons" element={<SuperAdminAccessRoute permissions={[SAP.ADDONS_VIEW, SAP.ADDONS_MANAGE]}><Addons /></SuperAdminAccessRoute>} />

    <Route path="/addon-modules" element={<SuperAdminAccessRoute permissions={[SAP.ADDONS_VIEW, SAP.ADDONS_MANAGE]}><Addons /></SuperAdminAccessRoute>} />

    <Route path="/plans" element={<SuperAdminAccessRoute permissions={[SAP.PLANS_VIEW, SAP.PLANS_MANAGE]}><Plans /></SuperAdminAccessRoute>} />

    <Route path="/subscriptions" element={<SuperAdminAccessRoute permissions={[SAP.SUBSCRIPTIONS_VIEW, SAP.SUBSCRIPTIONS_MANAGE]}><Subscriptions /></SuperAdminAccessRoute>} />

    <Route path="/settings" element={<SuperAdminAccessRoute permissions={[SAP.SETTINGS_VIEW, SAP.SETTINGS_MANAGE]}><Settings /></SuperAdminAccessRoute>} />

    <Route path="/system-settings" element={<SuperAdminAccessRoute permissions={[SAP.SETTINGS_VIEW, SAP.SETTINGS_MANAGE]}><Settings /></SuperAdminAccessRoute>} />

    <Route path="/reports" element={<SuperAdminAccessRoute permissions={[SAP.REPORTS_VIEW, SAP.REPORTS_EXPORT]}><Reports /></SuperAdminAccessRoute>} />

    <Route path="/superadmin-employees" element={<SuperAdminAccessRoute permissions={[SAP.EMPLOYEES_VIEW, SAP.EMPLOYEES_MANAGE, SAP.ROLES_VIEW, SAP.ROLES_MANAGE]}><SuperAdminEmployeeManagement /></SuperAdminAccessRoute>} />
    <Route path="/subscription-payments" element={<SuperAdminAccessRoute permissions={[SAP.PAYMENTS_VIEW, SAP.PAYMENTS_MANAGE, SAP.PAYMENTS_APPROVE, SAP.RECEIPTS_VIEW]}><PaymentsReceipts /></SuperAdminAccessRoute>} />
    <Route path="/superadmin-crm" element={<SuperAdminAccessRoute permissions={[SAP.CRM_VIEW, SAP.CRM_MANAGE]}><PlatformCRM /></SuperAdminAccessRoute>} />
    <Route path="/superadmin-support" element={<SuperAdminAccessRoute permissions={[SAP.SUPPORT_VIEW, SAP.SUPPORT_MANAGE]}><SuperAdminSupport /></SuperAdminAccessRoute>} />

    {/* Legacy super-admin placeholder URLs now resolve to working modules. */}
    <Route path="/users" element={
      <ProtectedRoute requireSuperAdmin={true}>
        <Navigate to="/companies" replace />
      </ProtectedRoute>
    } />

    <Route path="/roles" element={
      <ProtectedRoute requireSuperAdmin={true}>
        <Navigate to="/settings" replace />
      </ProtectedRoute>
    } />

    <Route path="/audit-logs" element={
      <ProtectedRoute requireSuperAdmin={true}>
        <Navigate to="/reports" replace />
      </ProtectedRoute>
    } />

    {/* ============================================
        COMPANY ADMIN ROUTES
    ============================================ */}

    {/* Dashboard */}
    <Route path="/company-dashboard" element={
      <CompanyAccessRoute>
        <BusinessSuiteLanding />
      </CompanyAccessRoute>
    } />

    {/* Stable business-suite entry aliases. Cold Storage internals remain on the existing routes. */}
    <Route path="/cold-storage" element={
      <CompanyAccessRoute>
        <BusinessSuiteLanding />
      </CompanyAccessRoute>
    } />

    <Route path="/both" element={
      <CompanyAccessRoute>
        <MixedSuiteLanding />
      </CompanyAccessRoute>
    } />

    <Route path="/mixed-dashboard" element={<Navigate to="/both" replace />} />
    <Route path="/both/cold-storage" element={<Navigate to="/both" replace />} />
    <Route path="/both/hd-orchids" element={<Navigate to="/hd-orchids" replace />} />

    {/* Company Settings */}
    <Route path="/hd-orchids/*" element={
      <CompanyAccessRoute>
        <HdOrchidsWorkspace />
      </CompanyAccessRoute>
    } />

    <Route path="/company-settings" element={
      <CompanyAccessRoute>
        <CompanySettings />
      </CompanyAccessRoute>
    } />

    <Route path="/branches" element={
      <CompanyAccessRoute anyOf={['branches_view','branches_manage','branches_modules_manage','branches_reports','settings_view']}>
        <BranchManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/branch-management" element={<Navigate to="/branches" replace />} />

    <Route path="/audit-trail" element={
      <CompanyAccessRoute anyOf={['audit_operations', 'workflow_audit_view', 'offline_server_audit']}>
        <AuditTrail />
      </CompanyAccessRoute>
    } />

    <Route path="/premium-addons" element={
      <CompanyAccessRoute>
        <PremiumAddons />
      </CompanyAccessRoute>
    } />

    <Route path="/support" element={
      <CompanyAccessRoute anyOf={['support_view','support_create','support_reply']}>
        <CompanySupport />
      </CompanyAccessRoute>
    } />

    <Route path="/customization" element={
      <CompanyAccessRoute anyOf={['customization_view','settings_view','settings_edit','custom_module_requests_create']}>
        <CustomizationCenter />
      </CompanyAccessRoute>
    } />

    <Route path="/subscription-receipts" element={
      <CompanyAccessRoute anyOf={['subscription_receipts_view','subscription_receipts_download','settings_view']}>
        <SubscriptionReceipts />
      </CompanyAccessRoute>
    } />

    <Route path="/form-field-settings" element={
      <CompanyAccessRoute permissions={['form_settings_edit','settings_edit']}>
        <FormFieldSettings />
      </CompanyAccessRoute>
    } />

    <Route path="/label-designer" element={
      <CompanyAccessRoute permissions={['form_settings_edit','settings_edit']}>
        <LabelDesigner />
      </CompanyAccessRoute>
    } />

    <Route path="/integrations" element={
      <CompanyAccessRoute permissions={['integrations_view','integrations_manage','settings_edit']}>
        <TenantIntegrations />
      </CompanyAccessRoute>
    } />

    <Route path="/crm" element={
      <CompanyAccessRoute permissions={['crm_manage']}>
        <ColdStorageCRM />
      </CompanyAccessRoute>
    } />

    <Route path="/service-providers" element={
      <CompanyAccessRoute permissions={['service_providers_view','service_providers_manage']}>
        <ServiceProviders />
      </CompanyAccessRoute>
    } />

    <Route path="/internal-documents" element={
      <CompanyAccessRoute permissions={['internal_documents_view','internal_documents_upload','internal_documents_share']}>
        <InternalDocuments />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        FINANCIAL YEAR MANAGEMENT ROUTES
    ============================================ */}

    <Route path="/financial-year" element={
      <CompanyAccessRoute>
        <FinancialYearManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/financial-year/create" element={
      <CompanyAccessRoute>
        <FinancialYearManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/financial-year/:id/edit" element={
      <CompanyAccessRoute>
        <FinancialYearManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/financial-year/:id" element={
      <CompanyAccessRoute>
        <FinancialYearManagement />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        GROWER MODULE ROUTES (Replaces Customers)
    ============================================ */}
    <Route path="/growers" element={
      <CompanyAccessRoute>
        <GrowerList />
      </CompanyAccessRoute>
    } />

    <Route path="/growers/add" element={
      <CompanyAccessRoute>
        <AddGrower />
      </CompanyAccessRoute>
    } />

    {/* Complete operational ledger: storage, grading, crates, payments and transfers. */}
    <Route path="/growers/ledger" element={
      <CompanyAccessRoute
        permissions={[
          'finance_operations',
          'finance_view',
          'billing_view',
          'invoices_view',
          'growers_view'
        ]}
      >
        <GrowerOperationsLedger />
      </CompanyAccessRoute>
    } />

    <Route path="/growers/:growerId/ledger" element={
      <CompanyAccessRoute
        permissions={[
          'finance_operations',
          'finance_view',
          'billing_view',
          'invoices_view',
          'growers_view'
        ]}
      >
        <GrowerOperationsLedger />
      </CompanyAccessRoute>
    } />

    <Route path="/grower-portal" element={
      <GrowerPortalAccessRoute>
        <GrowerPortalDashboard />
      </GrowerPortalAccessRoute>
    } />

    <Route path="/growers/:id" element={
      <CompanyAccessRoute>
        <GrowerDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/growers/:id/edit" element={
      <CompanyAccessRoute>
        <AddGrower />
      </CompanyAccessRoute>
    } />

    {/* Categories Module */}
    <Route path="/categories" element={
      <CompanyAccessRoute>
        <CategoryList />
      </CompanyAccessRoute>
    } />

    {/* Product Module */}
    <Route path="/products" element={
      <CompanyAccessRoute>
        <ProductList />
      </CompanyAccessRoute>
    } />

    <Route path="/products/add" element={
      <CompanyAccessRoute>
        <AddProduct />
      </CompanyAccessRoute>
    } />

    <Route path="/products/:id" element={
      <CompanyAccessRoute>
        <AddProduct />
      </CompanyAccessRoute>
    } />

    <Route path="/products/:id/edit" element={
      <CompanyAccessRoute>
        <AddProduct />
      </CompanyAccessRoute>
    } />

    {/* Legacy Dispatch URLs now use the unified Outward workflow */}
    <Route path="/dispatch" element={<RedirectWithSearch to="/outward" />} />
    <Route path="/dispatch/create" element={<RedirectWithSearch to="/outward/request" />} />
    <Route path="/dispatch/:id" element={<RedirectOutwardById />} />
    <Route path="/dispatch/:id/edit" element={<RedirectOutwardById process />} />

    {/* Warehouse Module */}
    <Route path="/warehouses" element={
      <CompanyAccessRoute>
        <WarehouseList />
      </CompanyAccessRoute>
    } />

    {/* Warehouse Chambers Routes */}
    <Route path="/warehouses/:id/chambers" element={
      <CompanyAccessRoute>
        <ChamberLocationManager />
      </CompanyAccessRoute>
    } />

    <Route path="/warehouses/:id/chambers/create" element={
      <CompanyAccessRoute>
        <ChamberLocationManager />
      </CompanyAccessRoute>
    } />

    <Route path="/warehouses/:id/chambers/:chamberId" element={
      <CompanyAccessRoute>
        <ChamberLocationManager />
      </CompanyAccessRoute>
    } />

    <Route path="/warehouses/:id/chambers/:chamberId/edit" element={
      <CompanyAccessRoute>
        <ChamberLocationManager />
      </CompanyAccessRoute>
    } />

    <Route path="/chambers" element={
      <CompanyAccessRoute>
        <ChamberLocationManager />
      </CompanyAccessRoute>
    } />

    {/* Trading Module
        V2.6: Trading pages own exactly ONE MainLayout.
        App must NOT wrap these routes in another MainLayout. */}
    <Route path="/trading" element={
      <CompanyAccessRoute>
        <TradingDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/trading/create" element={
      <CompanyAccessRoute>
        <TradeForm />
      </CompanyAccessRoute>
    } />

    {/* Keep BEFORE /trading/:id so "reports" is not interpreted as a trade ID. */}
    <Route path="/trading/reports" element={
      <CompanyAccessRoute>
        <TradingReports />
      </CompanyAccessRoute>
    } />

    <Route path="/trading/:id/edit" element={
      <CompanyAccessRoute>
        <TradeForm />
      </CompanyAccessRoute>
    } />

    <Route path="/trading/:id" element={
      <CompanyAccessRoute>
        <TradeForm />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        AGREEMENT MANAGEMENT ROUTES
    ============================================ */}

   {/* ============================================
        AGREEMENT MANAGEMENT ROUTES
        Backward-compatible permissions:
        - New roles use agreements_* permissions.
        - Older Grower/Customer roles can keep their equivalent CRUD access.
    ============================================ */}

    <Route
      path="/agreements"
      element={
        <CompanyAccessRoute
          permissions={[
            'agreements_view',
            'agreements_create',
            'agreements_edit',
            'customers_view',
            'customers_create',
            'customers_edit',
            'growers_view',
            'growers_create',
            'growers_edit'
          ]}
        >
          <AgreementList />
        </CompanyAccessRoute>
      }
    />

    <Route
      path="/agreements/create"
      element={
        <CompanyAccessRoute
          permissions={[
            'agreements_create',
            'customers_create',
            'growers_create'
          ]}
        >
          <CreateAgreement />
        </CompanyAccessRoute>
      }
    />

    {/* View Agreement - IMPORTANT: This must come BEFORE the edit route */}
    <Route
      path="/agreements/:id"
      element={
        <CompanyAccessRoute
          permissions={[
            'agreements_view',
            'agreements_create',
            'agreements_edit',
            'customers_view',
            'customers_create',
            'customers_edit',
            'growers_view',
            'growers_create',
            'growers_edit'
          ]}
        >
          <AgreementView />
        </CompanyAccessRoute>
      }
    />

    <Route
      path="/agreements/:id/edit"
      element={
        <CompanyAccessRoute
          permissions={[
            'agreements_edit',
            'customers_edit',
            'growers_edit'
          ]}
        >
          <CreateAgreement />
        </CompanyAccessRoute>
      }
    />

    {/* ============================================
        INVENTORY MODULE ROUTES
    ============================================ */}
    <Route path="/inventory" element={
      <CompanyAccessRoute>
        <InventoryList />
      </CompanyAccessRoute>
    } />

    {/* Dynamic grading/packing consumables used by the Outward grading line. */}
    <Route path="/stock" element={
      <CompanyAccessRoute
        permissions={[
          'stock_view',
          'stock_manage',
          'inventory_view',
          'inventory_create',
          'inventory_edit',
          'inventory_adjust'
        ]}
      >
        <StockManagement initialTab="dashboard" />
      </CompanyAccessRoute>
    } />

    {/* Dedicated Stock Reports route opens the report/ledger tab. */}
    <Route path="/stock/reports" element={
      <CompanyAccessRoute permissions={['stock_reports','stock_view','stock_manage','inventory_view','reports_view']}>
        <StockManagement initialTab="reports" />
      </CompanyAccessRoute>
    } />

    <Route path="/stock/suppliers" element={
      <CompanyAccessRoute permissions={['stock_ledger','stock_view','stock_manage','finance_view','inventory_view']}>
        <StockManagement initialTab="supplier" />
      </CompanyAccessRoute>
    } />

    <Route path="/stock/transfer" element={
      <CompanyAccessRoute
        permissions={[
          'stock_transfer',
          'inventory_transfer',
          'inventory_edit',
          'outward_create',
          'trading_create'
        ]}
      >
        <StockOwnershipTransfer />
      </CompanyAccessRoute>
    } />

    {/* Legacy Inventory URLs now redirect to their dedicated modules. */}
    <Route path="/inventory/inward" element={<Navigate to="/inward" replace />} />
    <Route path="/inventory/outward" element={<Navigate to="/outward" replace />} />
    <Route path="/inventory/outward/reports" element={<Navigate to="/outward/reports" replace />} />

    {/* ============================================
        OUTWARD MANAGEMENT - ROLE BASED FLOW
        Request -> Approval -> Storage Retrieval -> Grading/Packing -> Finance -> Outward Gate Pass -> Dispatch
    ============================================ */}
    <Route path="/outward" element={
      <CompanyAccessRoute>
        <OutwardDashboard />
      </CompanyAccessRoute>
    } />

    {/* Grower / store outward request registration. This is intentionally
        separate from the operational process page. The request chooses the
        stored lots and vehicle/driver details before any lot is retrieved. */}
    <Route path="/outward/request" element={
      <CompanyAccessRoute>
        <OutwardRequest />
      </CompanyAccessRoute>
    } />

    {/* Legacy create URL now opens the request desk. */}
    <Route path="/outward/new" element={<RedirectWithSearch to="/outward/request" />} />

    {/* Compatibility aliases for older links/bookmarks. */}
    <Route path="/create-outward" element={<RedirectWithSearch to="/outward/request" />} />
    <Route path="/outward/grading" element={<CompanyAccessRoute permissions={['outward_grading_view','outward_grading_process','outward_grading_edit','outward_grading_verify','grading_view','grading_operations']}><GradingLineRuns /></CompanyAccessRoute>} />
    <Route path="/outward/billing" element={<RedirectWithSearch to="/outward" />} />
    <Route path="/outward/payment" element={<RedirectWithSearch to="/outward" />} />
    <Route path="/outward/gatepass" element={<RedirectWithSearch to="/outward" />} />
    <Route path="/outward/:id/grading" element={<RedirectOutwardById process />} />
    <Route path="/outward/:id/billing" element={<RedirectOutwardById process />} />
    <Route path="/outward/:id/payment" element={<RedirectOutwardById process />} />
    <Route path="/outward/:id/gatepass" element={<RedirectOutwardById process />} />



    <Route path="/outward/reports" element={
      <CompanyAccessRoute
        permissions={[
          'outward_view', 'dispatch_view', 'inventory_view', 'reports_view',
          'outward_edit', 'outward_delete'
        ]}
      >
        <OutwardReports />
      </CompanyAccessRoute>
    } />

    <Route path="/outward/:id/grading-receipt" element={
      <CompanyAccessRoute>
        <GradingReceipt />
      </CompanyAccessRoute>
    } />

    <Route path="/outward/:id/process" element={
      <CompanyAccessRoute>
        <OutwardProcess />
      </CompanyAccessRoute>
    } />

    <Route path="/outward/:id" element={
      <CompanyAccessRoute>
        <OutwardDetail />
      </CompanyAccessRoute>
    } />

    <Route path="/inventory/transfer" element={
      <CompanyAccessRoute>
        <Transfer />
      </CompanyAccessRoute>
    } />

    <Route path="/inventory/adjustment" element={
      <CompanyAccessRoute>
        <Adjustment />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        INWARD MANAGEMENT - ROLE BASED FLOW
        Booking -> Gate Entry -> Weighment -> Unloading & Inspection -> Lot -> Storage
    ============================================ */}

    {/* Inward / Aamad main page */}
    <Route path="/inward" element={
      <CompanyAccessRoute>
        <Inward />
      </CompanyAccessRoute>
    } />

    {/* Inward Dashboard */}
    <Route path="/inward/dashboard" element={
      <CompanyAccessRoute>
        <InwardDashboard />
      </CompanyAccessRoute>
    } />

    {/* Dynamic grouped operator workbench. Business stages remain separate. */}
    <Route path="/inward/workbench" element={
      <CompanyAccessRoute permissions={['workflow_combined_execute','inward_operations','inward_admin']}>
        <InwardWorkflowWorkbench />
      </CompanyAccessRoute>
    } />

    {/* Public Inward Request Management - STAFF ONLY
        Canonical staff verification route. This is intentionally distinct
        from the public customer form at /public/inward-request. */}
    <Route
      path="/inward/public-request-management"
      element={
        <CompanyAccessRoute>
          <PublicInwardRequests />
        </CompanyAccessRoute>
      }
    />

    {/* Legacy staff URL -> canonical verification desk */}
    <Route
      path="/inward/public-requests"
      element={
        <Navigate
          to="/inward/public-request-management"
          replace
        />
      }
    />

    {/* Extra compatibility alias for old bookmarks */}
    <Route
      path="/inward/public-requests/manage"
      element={
        <Navigate
          to="/inward/public-request-management"
          replace
        />
      }
    />

    {/* Step 1: Gate Entry - Gatekeeper Role */}
    <Route path="/inward/gate-entry" element={
      <CompanyAccessRoute>
        <GateEntry />
      </CompanyAccessRoute>
    } />

    {/* Step 2: Weighment - Weighbridge Operator Role */}
    <Route path="/inward/weighment" element={
      <CompanyAccessRoute>
        <CombinedWeighmentInspection initialTab="weighment" />
      </CompanyAccessRoute>
    } />

    {/* Step 3: Combined Unloading & Inspection - Unloading/Inspection Role */}
    <Route path="/inward/unloading" element={
      <CompanyAccessRoute>
        <CombinedWeighmentInspection initialTab="unloading" />
      </CompanyAccessRoute>
    } />

    {/* Standalone Inspection was merged into Unloading. Preserve old bookmarks by redirecting. */}
    <Route path="/inward/inspection" element={<RedirectWithSearch to="/inward/unloading" />} />
    <Route path="/inward/unloading-inspection" element={<RedirectWithSearch to="/inward/unloading" />} />
    <Route path="/inward/quality-inspection" element={<RedirectWithSearch to="/inward/unloading" />} />
    <Route path="/inward/storage-allocation" element={<RedirectWithSearch to="/inward/storage" />} />

    {/* Agreement-controlled Inward processing stages. */}
    <Route path="/inward/grading" element={<CompanyAccessRoute><InwardGrading /></CompanyAccessRoute>} />
    <Route path="/inward/finance" element={<CompanyAccessRoute><InwardFinance /></CompanyAccessRoute>} />
    <Route path="/inward/packing" element={<CompanyAccessRoute><InwardPacking /></CompanyAccessRoute>} />

    {/* Lot Creation - Store Keeper Role */}
    <Route path="/inward/lot-creation" element={
      <CompanyAccessRoute>
        <LotCreation />
      </CompanyAccessRoute>
    } />

    {/* Storage Allocation - final inward step; receipt + QR are generated here */}
    <Route path="/inward/storage" element={
      <CompanyAccessRoute>
        <StorageAllocation />
      </CompanyAccessRoute>
    } />

    {/* Receipt is generated automatically by successful Storage Allocation. */}
    <Route
      path="/inward/receipt"
      element={<RedirectWithSearch to="/inward/storage" />}
    />
    <Route path="/inward/:id/grading" element={<RedirectInwardById />} />
    <Route path="/inward/:id/receipt" element={<RedirectInwardById />} />

    {/* Inward Process - declared after the static step routes */}
    <Route path="/inward/:id/process" element={
      <CompanyAccessRoute>
        <InwardProcess />
      </CompanyAccessRoute>
    } />

    {/* Inward Detail View - dynamic, declared last in this group */}
    <Route path="/inward/:id" element={
      <CompanyAccessRoute>
        <InwardDetail />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        STORAGE MANAGEMENT MODULE - BIN ROUTES
    ============================================ */}

    <Route path="/storage/dashboard" element={
      <CompanyAccessRoute>
        <StorageDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/storage/bins" element={
      <CompanyAccessRoute>
        <BinManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/storage/bins/dashboard" element={
      <CompanyAccessRoute>
        <BinDashboard />
      </CompanyAccessRoute>
    } />

    {/* Bulk create declared BEFORE /storage/bins/:binId */}
    <Route path="/storage/bins/bulk-create" element={
      <CompanyAccessRoute>
        <BulkCreateBinsPage />
      </CompanyAccessRoute>
    } />

    <Route path="/storage/bins/:binId" element={
      <CompanyAccessRoute>
        <BinDetail />
      </CompanyAccessRoute>
    } />

    <Route path="/storage/bins/:binId/edit" element={
      <CompanyAccessRoute>
        <BinManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/storage/crates" element={
      <CompanyAccessRoute>
        <CrateManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/storage/crates/ledger" element={
      <CompanyAccessRoute
        permissions={[
          'crate_ledger_view', 'crate_print', 'crate_reports', 'crate_operations'
        ]}
      >
        <CrateLedger />
      </CompanyAccessRoute>
    } />

    <Route path="/crate-reconciliation" element={
      <CompanyAccessRoute
        permissions={[
          'crate_ledger_view', 'crate_print', 'crate_reports', 'crate_operations'
        ]}
      >
        <CrateReconciliation />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        INVOICE MODULE ROUTES
    ============================================ */}

    <Route path="/invoices" element={
      <CompanyAccessRoute>
        <InvoiceList />
      </CompanyAccessRoute>
    } />

    <Route path="/invoices/create" element={
      <CompanyAccessRoute>
        <CreateInvoice />
      </CompanyAccessRoute>
    } />

    <Route path="/invoices/:id" element={
      <CompanyAccessRoute>
        <InvoiceDetail />
      </CompanyAccessRoute>
    } />

    <Route path="/invoices/:id/edit" element={
      <CompanyAccessRoute>
        <CreateInvoice />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        FINANCE MODULE ROUTES - NEW CONSOLIDATED MODULE
        Dashboard / Billing / Receipts / Payments /
        Ledger / Expenses / Accounts / Reports / Payroll
    ============================================ */}

    <Route path="/finance" element={
      <CompanyAccessRoute>
        <FinanceDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/dashboard" element={
      <CompanyAccessRoute>
        <FinanceDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/invoices" element={
      <CompanyAccessRoute>
        <FinanceInvoices />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/receipts" element={
      <CompanyAccessRoute>
        <FinanceReceipts />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/payments" element={
      <CompanyAccessRoute>
        <FinancePayments />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/ledger" element={
      <CompanyAccessRoute>
        <FinanceGrowerLedger />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/receivables" element={
      <CompanyAccessRoute>
        <FinanceGrowerLedger />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/outstanding" element={
      <CompanyAccessRoute>
        <FinanceGrowerLedger />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/expenses" element={
      <CompanyAccessRoute>
        <FinanceExpenses />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/accounts" element={
      <CompanyAccessRoute>
        <FinanceAccounts />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/reports" element={
      <Navigate to="/company/reports/finance" replace />
    } />

    <Route path="/finance/reconciliation" element={
      <CompanyAccessRoute anyOf={['bank_reconciliation_view','bank_reconciliation_manage','finance_operations']}>
        <BankReconciliation />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/employee-advances" element={
      <CompanyAccessRoute permissions={['finance_operations','payroll_operations']}>
        <EmployeeAdvancesLoans />
      </CompanyAccessRoute>
    } />

    <Route path="/whatsapp-automation" element={
      <CompanyAccessRoute permissions={['whatsapp_automation_manage','settings_edit']}>
        <NotificationSettings />
      </CompanyAccessRoute>
    } />

    <Route path="/finance/cheques" element={
      <Navigate to="/finance/reconciliation" replace />
    } />

    <Route path="/finance/payroll" element={
      <CompanyAccessRoute>
        <PayrollManagement />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        COMPANY REPORTING MODULE - ROLE BASED
        Super Admin /reports remains unchanged.
        Company reports use /company/reports/*
    ============================================ */}
    <Route
      path="/company/reports"
      element={
        <CompanyAccessRoute anyOf={['reports_view','reports_generate','reports_management','reports_inward','reports_gate','reports_quality','reports_lots','reports_storage','reports_inventory','reports_outward','reports_growers','reports_trading','reports_finance','reports_hr','finance_reports']}>
          <ReportsDashboard />
        </CompanyAccessRoute>
      }
    />

    <Route
      path="/company/reports/grower-center"
      element={
        <CompanyAccessRoute anyOf={['reports_view','reports_generate','reports_growers']}>
          <GrowerReportCenter />
        </CompanyAccessRoute>
      }
    />

    <Route
      path="/company/reports/finance"
      element={
        <CompanyAccessRoute anyOf={['reports_view','reports_generate','reports_finance','finance_reports','finance_view','finance_operations']}>
          <FinanceReportsDashboard />
        </CompanyAccessRoute>
      }
    />

    {COMPANY_REPORT_ROUTES.map(
      ({
        key,
        path,
        Component,
        allOf,
        anyOf,
      }) => (
        <Route
          key={key}
          path={path}
          element={
            <CompanyAccessRoute
              allOf={allOf}
              anyOf={[
                ...(Array.isArray(anyOf) ? anyOf : []),
                ...(Array.isArray(allOf)
                  ? allOf.filter((permission) => permission !== 'reports_view' && String(permission || '').startsWith('reports_'))
                  : []),
              ]}
            >
              <Component />
            </CompanyAccessRoute>
          }
        />
      )
    )}

    {/* ============================================
        EMPLOYEE MODULE ROUTES
    ============================================ */}

    <Route path="/employees" element={
      <CompanyAccessRoute>
        <EmployeeManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/employees/list" element={<Navigate to="/employees" replace />} />

    <Route path="/employees/create" element={
      <CompanyAccessRoute>
        <MainLayout title="Create Employee"><EmployeeFormWrapper /></MainLayout>
      </CompanyAccessRoute>
    } />

    {/* Static employee routes declared BEFORE /employees/:id */}
    <Route path="/employees/stats" element={
      <CompanyAccessRoute>
        <EmployeeStats />
      </CompanyAccessRoute>
    } />

    <Route path="/employees/payroll" element={
      <CompanyAccessRoute>
        <PayrollManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/employees/unloading-work" element={
      <CompanyAccessRoute>
        <UnloadingWorkDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/employees/advances-loans" element={
      <CompanyAccessRoute permissions={['employees_view','payroll_operations','finance_operations']}>
        <EmployeeAdvancesLoans />
      </CompanyAccessRoute>
    } />

    <Route path="/employees/:id" element={
      <CompanyAccessRoute>
        <EmployeeViewPage />
      </CompanyAccessRoute>
    } />

    <Route path="/employees/:id/edit" element={
      <CompanyAccessRoute>
        <MainLayout title="Edit Employee"><EmployeeFormWrapper /></MainLayout>
      </CompanyAccessRoute>
    } />

    <Route path="/employees/:id/payroll" element={
      <CompanyAccessRoute>
        <PayrollManagement />
      </CompanyAccessRoute>
    } />

    {/* Attendance Management */}
    <Route path="/attendance" element={
      <CompanyAccessRoute>
        <AttendanceDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/attendance/mark" element={
      <CompanyAccessRoute>
        <AttendanceDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/attendance/bulk" element={
      <CompanyAccessRoute>
        <AttendanceDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/attendance/reports" element={
      <CompanyAccessRoute>
        <AttendanceDashboard />
      </CompanyAccessRoute>
    } />

    <Route path="/attendance/selfie" element={
      <CompanyAccessRoute>
        <SelfieAttendance />
      </CompanyAccessRoute>
    } />


    <Route path="/attendance/selfie-report" element={
      <CompanyAccessRoute>
        <SelfieAttendanceReport />
      </CompanyAccessRoute>
    } />

    {/* Payroll Management */}
    <Route path="/payroll" element={
      <CompanyAccessRoute>
        <PayrollManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/payroll/process" element={
      <CompanyAccessRoute>
        <PayrollManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/payroll/reports" element={
      <CompanyAccessRoute>
        <PayrollManagement />
      </CompanyAccessRoute>
    } />

    <Route path="/payroll/slips" element={
      <CompanyAccessRoute>
        <PayrollManagement />
      </CompanyAccessRoute>
    } />

    {/* ============================================
        COMPANY NOTIFICATIONS
    ============================================ */}
    <Route
      path="/notifications"
      element={
        <CompanyAccessRoute permissions={['notifications_view','notifications_manage']}>
          <NotificationCenter />
        </CompanyAccessRoute>
      }
    />

    <Route
      path="/notification-settings"
      element={
        <CompanyAccessRoute permissions={['notifications_settings','notifications_manage']}>
          <NotificationSettings />
        </CompanyAccessRoute>
      }
    />

    {/* ============================================
        TEMPERATURE / IOT MONITORING MODULE
    ============================================ */}
    <Route
      path="/temperature"
      element={
        <CompanyAccessRoute permissions={['temperature_view']}>
          <TemperatureDashboard />
        </CompanyAccessRoute>
      }
    />

    <Route
      path="/temperature/sensors"
      element={
        <CompanyAccessRoute
          anyOf={[
            'temperature_configure',
            'temperature_edit',
            'temperature_device_manage'
          ]}
        >
          <SensorConfiguration />
        </CompanyAccessRoute>
      }
    />

    <Route
      path="/temperature/alerts"
      element={
        <CompanyAccessRoute
          allOf={['temperature_view']}
          anyOf={[
            'temperature_alerts',
            'temperature_acknowledge',
            'temperature_edit'
          ]}
        >
          <TemperatureAlerts />
        </CompanyAccessRoute>
      }
    />

    {/* Historical placeholder module URLs are retained only as safe compatibility redirects. */}
    <Route path="/maintenance" element={<Navigate to="/company-dashboard" replace />} />
    <Route path="/services" element={<Navigate to="/company-dashboard" replace />} />
    <Route path="/deployment" element={<Navigate to="/company-dashboard" replace />} />

    {/* ============================================
        REDIRECTS
    ============================================ */}
    <Route path="/warehouse" element={<Navigate to="/warehouses" replace />} />
    <Route path="/dispatcher" element={<Navigate to="/outward" replace />} />
    <Route path="/storage" element={<Navigate to="/storage/dashboard" replace />} />
    <Route path="/crates" element={<Navigate to="/storage/crates" replace />} />
    <Route path="/bins" element={<Navigate to="/storage/bins" replace />} />
    <Route path="/bin-management" element={<Navigate to="/storage/bins" replace />} />
    <Route path="/bin-dashboard" element={<Navigate to="/storage/bins/dashboard" replace />} />
    <Route path="/inventory/bins" element={<RedirectWithSearch to="/storage/bins" />} />
    <Route path="/inventory/bins/dashboard" element={<RedirectWithSearch to="/storage/bins/dashboard" />} />
    <Route path="/inventory/bins/bulk-create" element={<RedirectWithSearch to="/storage/bins/bulk-create" />} />
    <Route path="/inventory/bins/:binId/edit" element={<RedirectStorageBin edit />} />
    <Route path="/inventory/bins/:binId" element={<RedirectStorageBin />} />
    <Route path="/warehouse/chamber-locations" element={<RedirectWithSearch to="/chambers" />} />
    <Route path="/inventory/stock" element={<RedirectWithSearch to="/stock" />} />
    <Route path="/stock-management" element={<RedirectWithSearch to="/stock" />} />
    <Route path="/stock-reports" element={<RedirectWithSearch to="/stock/reports" />} />
    <Route path="/supplier-ledger" element={<RedirectWithSearch to="/stock/suppliers" />} />
    <Route path="/inventory/stock/transfer" element={<RedirectWithSearch to="/stock/transfer" />} />
    <Route path="/stock-transfer" element={<RedirectWithSearch to="/stock/transfer" />} />
    <Route path="/crate-ledger" element={<RedirectWithSearch to="/storage/crates/ledger" />} />
    <Route path="/grower-ledger" element={<RedirectWithSearch to="/finance/ledger" />} />

    <Route path="/hr" element={<Navigate to="/employees" replace />} />
    <Route path="/hr/employees" element={<Navigate to="/employees" replace />} />
    <Route path="/hr/attendance" element={<Navigate to="/attendance" replace />} />
    <Route path="/hr/payroll" element={<Navigate to="/payroll" replace />} />

    {/* Customer Redirect to Growers.
        The :id variants use a wrapper because <Navigate> cannot interpolate
        route params - a literal "/growers/:id" would hit the catch-all. */}
    <Route path="/customers" element={<Navigate to="/growers" replace />} />
    <Route path="/customers/add" element={<Navigate to="/growers/add" replace />} />
    <Route path="/customers/:id" element={<RedirectGrower />} />
    <Route path="/customers/:id/edit" element={<RedirectGrower suffix="/edit" />} />

    {/* Financial Year Redirects */}
    <Route path="/financial-years" element={<Navigate to="/financial-year" replace />} />
    <Route path="/fy" element={<Navigate to="/financial-year" replace />} />

    {/* Authentication Portal Compatibility Redirects */}
    <Route
      path="/company"
      element={<Navigate to="/login/storelogin" replace />}
    />

    <Route
      path="/superadmin"
      element={<Navigate to="/admin/superadmin/login" replace />}
    />

    {/* ============================================
        404 FALLBACK
    ============================================ */}
    <Route path="*" element={<NotFoundRedirect />} />
  </Routes>
);

function App() {
  return (
    <AuthProvider>
      <OfflineSyncProvider>
      <ThemeProvider>
      <FinancialYearProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<RouteLoadingFallback />}>
            <AppRoutes />
          </Suspense>
        </Router>
      </FinancialYearProvider>
      </ThemeProvider>
      </OfflineSyncProvider>
    </AuthProvider>
  );
}

export default App;
